import { computed, reactive, ref, toValue, watch, type MaybeRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import type { CoachFeedbackGoal } from './use-coach-feedback-goal'
import type { PerspectiveTarget } from './use-create-coach-feedback-goal'

type PutCoachFeedbackGoalPayload = {
  feedbackTopicId: string
  coacheeIds: string[]
  perspectiveTargets: PerspectiveTarget[]
  isActive: boolean
}

export const useEditCoachFeedbackGoalMembers = (
  feedbackGoal: MaybeRef<CoachFeedbackGoal>,
  isOpen: Ref<boolean>,
  onClose?: () => void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const form = reactive<{ feedbackSenderIds: string[] }>({
    feedbackSenderIds: [],
  })
  const errors = ref<Map<'feedbackSenderIds', string>>(new Map())

  const { mutate: updateGoal, status: updateStatus } = usePut<void, PutCoachFeedbackGoalPayload>(
    `coaching/feedback-goals/${toValue(feedbackGoal).id}`,
  )

  const resetFromGoal = () => {
    form.feedbackSenderIds = toValue(feedbackGoal).coachees.map((item) => item.coachee.id)
    errors.value = new Map()
  }

  const saveMembers = async () => {
    errors.value = new Map()
    if (form.feedbackSenderIds.length === 0) {
      errors.value.set('feedbackSenderIds', t('feedback.formValidation.members'))
      return
    }

    const goal = toValue(feedbackGoal)
    await updateGoal({
      feedbackTopicId: goal.feedbackTopic.id,
      coacheeIds: form.feedbackSenderIds,
      perspectiveTargets: [...goal.perspectiveTargets],
      isActive: true,
    })

    if (updateStatus.value === ApiRequestStatus.Success) {
      onClose?.()
      snackStore.showInfo(t('coaching.feedbackGoals.editSuccess'))
      return
    }
    snackStore.showError(t('coaching.feedbackGoals.editError'))
  }

  watch(isOpen, (open) => {
    if (open) {
      resetFromGoal()
    }
  })

  const isSaving = computed(() => updateStatus.value === ApiRequestStatus.Loading)

  return {
    form,
    errors,
    isSaving,
    saveMembers,
    resetFromGoal,
  }
}
